import { getBridgeRoot } from '../cwd/index.js';
import {
  deleteResolveCacheEntry,
  getResolveCacheEntry,
  resolveCacheKey,
  statMatchesCacheSync,
} from './resolve-file-cache.js';
import { joinInflightResolve } from './resolve-inflight.js';
import {
  resolveFileForReadImpl,
  resolveFileForReadImplSync,
  type ResolveFileForReadResult,
} from './resolve-file-for-read-core.js';

/** Synchronous resolve for interactive main-thread reads (avoids libuv pool contention). */
export function resolveFileForReadOnMainThread(
  relativePath: string,
  sessionParentPath: string = getBridgeRoot(),
): ResolveFileForReadResult {
  const cacheKey = resolveCacheKey(sessionParentPath, relativePath);
  const cached = getResolveCacheEntry(cacheKey);
  if (cached != null) {
    if (statMatchesCacheSync(cached)) {
      return { path: cached.path, size: cached.size };
    }
    deleteResolveCacheEntry(cacheKey);
  }

  return resolveFileForReadImplSync(relativePath, sessionParentPath);
}

export async function resolveFileForReadAsync(
  relativePath: string,
  sessionParentPath: string = getBridgeRoot(),
): Promise<ResolveFileForReadResult> {
  const cacheKey = resolveCacheKey(sessionParentPath, relativePath);
  const cached = getResolveCacheEntry(cacheKey);
  if (cached != null) {
    if (statMatchesCacheSync(cached)) {
      return { path: cached.path, size: cached.size };
    }
    deleteResolveCacheEntry(cacheKey);
  }

  return joinInflightResolve(cacheKey, () => resolveFileForReadImpl(relativePath, sessionParentPath));
}

/** Resolve path only (legacy callers). Prefer {@link resolveFileForReadAsync} when size is needed. */
export async function resolveFilePathAsync(
  relativePath: string,
  sessionParentPath: string = getBridgeRoot(),
): Promise<string | { error: string }> {
  const result = await resolveFileForReadAsync(relativePath, sessionParentPath);
  if ('error' in result) return result;
  return result.path;
}
